import { Button, Col, DatePicker, Form, Input, Modal, notification, Radio, Row, Select, TimePicker } from "antd";
import moment from "moment";
import { ClockCircleOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useEffect, useState } from "react";
import { addAppointmentApi, listOfClientApi, updateAppointmentApi } from "../../../config/Api";
import { apiErrorMessage } from "../../../services/ApiError";

export const APPOINTMENT_TYPES = ['Call', 'Video', 'In person']
const TIME_FORMAT = 'HH:mm'
// accepted while typing a time: 14:30, 9:05, 1430, 905
const TIME_INPUT_FORMATS = ['HH:mm', 'H:mm', 'HHmm', 'Hmm']
const DEFAULT_LENGTH_MINUTES = 30

// book a new appointment, or reschedule one (appointment prop)
// patientId: pre-select the patient (booking from the patient page)
export const BookAppointment = ({ open, onClose, onSaved, appointment, patientId }) => {
    const [form] = Form.useForm()
    const [patients, setPatients] = useState([])
    const [saving, setSaving] = useState(false)
    const isEdit = Boolean(appointment)

    // contact details of the chosen patient, shown under the patient field
    const selectedPatientId = Form.useWatch('patient_id', form)
    const contact = isEdit
        ? { email: appointment.patient_email, mobile: appointment.patient_mobile, preferred_call_time: appointment.preferred_call_time }
        : patients.find((patient) => patient.id === selectedPatientId)

    // appointments can only be booked for your own active patients (the admin's list has everyone's)
    useEffect(() => {
        if (!open || isEdit) return
        listOfClientApi()
            .then((res) => {
                const myId = localStorage.getItem('id')
                setPatients((res.data.data || []).filter((patient) => patient.user_id == myId && patient.status))
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
    }, [open, isEdit])

    useEffect(() => {
        if (!open) return
        form.resetFields()
        if (isEdit) {
            form.setFieldsValue({
                date: moment(appointment.date, 'YYYY-MM-DD'),
                start_time: moment(appointment.start_time, TIME_FORMAT),
                end_time: moment(appointment.end_time, TIME_FORMAT),
                type: appointment.type,
                notes: appointment.notes,
            })
        } else {
            form.setFieldsValue({ patient_id: patientId ? Number(patientId) : undefined, type: 'Call' })
        }
    }, [open, appointment, patientId])

    // antd time pickers only keep a value after OK; Tab or a click elsewhere would drop it.
    // So a picked or typed time is saved to the form straight away.
    const setTime = (field, value) => {
        if (!value) return
        const time = value.clone().seconds(0)
        form.setFieldsValue({ [field]: time })
        // a new start with no end yet gets the usual length
        if (field === 'start_time' && !form.getFieldValue('end_time')) {
            form.setFieldsValue({ end_time: time.clone().add(DEFAULT_LENGTH_MINUTES, 'minutes') })
        }
        form.validateFields(['end_time']).catch(() => {})
    }
    const keepTypedTime = (field) => (e) => {
        const typed = moment((e.target.value || '').trim(), TIME_INPUT_FORMATS, true)
        if (typed.isValid()) setTime(field, typed)
    }

    const onFinish = (values) => {
        const data = {
            date: values.date.format('YYYY-MM-DD'),
            start_time: values.start_time.format(TIME_FORMAT),
            end_time: values.end_time.format(TIME_FORMAT),
            type: values.type,
            notes: values.notes || '',
        }
        setSaving(true)
        const request = isEdit
            ? updateAppointmentApi(appointment.id, data)
            : addAppointmentApi({ ...data, patient_id: values.patient_id })
        request
            .then((res) => {
                notification.success({ message: res.data.message, duration: 8 })
                onSaved && onSaved(res.data.data)
                onClose()
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
            .finally(() => setSaving(false))
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={560}
            title={isEdit ? `Reschedule appointment with ${appointment.patient_name}` : 'New Appointment'}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={onFinish} name="bookAppointmentForm">
                <Row gutter={16}>
                    {!isEdit && (
                        <Col span={24}>
                            <Form.Item
                                name="patient_id"
                                label="Patient"
                                rules={[{ required: true, message: 'Please select a patient!' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Search by name, email or phone"
                                    // the field shows just the name once chosen; the list shows name, email and phone
                                    optionLabelProp="label"
                                    filterOption={(input, option) => {
                                        const text = input.trim().toLowerCase()
                                        return [option.label, option.email, option.mobile]
                                            .some((value) => String(value || '').toLowerCase().includes(text))
                                    }}
                                    disabled={Boolean(patientId)}
                                    notFoundContent="No matching active patients"
                                >
                                    {patients.map((patient) => (
                                        <Select.Option
                                            key={patient.id}
                                            value={patient.id}
                                            label={patient.name}
                                            email={patient.email}
                                            mobile={patient.mobile}
                                        >
                                            <div>{patient.name}</div>
                                            <div className="text-muted" style={{ fontSize: 12 }}>
                                                {patient.email || 'No email'} · {patient.mobile || 'No phone'}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}
                    {contact && (
                        <Col span={24}>
                            <div className="mb-3 p-2 rounded" style={{ background: '#f5f6f8' }} id="appointmentPatientContact">
                                <div><MailOutlined className="me-2" />{contact.email || 'No email'}</div>
                                <div><PhoneOutlined className="me-2" />{contact.mobile || 'No phone number'}</div>
                                {/* view only: set in the patient's Personal Details */}
                                <div>
                                    <ClockCircleOutlined className="me-2" />
                                    Preferred call time: {contact.preferred_call_time
                                        ? <b>{contact.preferred_call_time}</b>
                                        : <span className="text-danger">Not set</span>}
                                </div>
                            </div>
                        </Col>
                    )}
                    <Col xs={24} md={10}>
                        <Form.Item
                            name="date"
                            label="Date"
                            rules={[{ required: true, message: 'Please select a date!' }]}
                        >
                            <DatePicker
                                className="w-100"
                                format="DD-MM-YYYY"
                                disabledDate={(current) => current && current < moment().startOf('day')}
                            />
                        </Form.Item>
                    </Col>
                    {['start_time', 'end_time'].map((field) => (
                        <Col xs={12} md={7} key={field}>
                            <Form.Item
                                name={field}
                                label={field === 'start_time' ? 'Start' : 'End'}
                                dependencies={field === 'end_time' ? ['start_time'] : undefined}
                                rules={[
                                    { required: true, message: field === 'start_time' ? 'Please select a start time!' : 'Please select an end time!' },
                                    field === 'end_time' && (({ getFieldValue }) => ({
                                        validator: (_, value) => {
                                            const start = getFieldValue('start_time')
                                            return !value || !start || value.isAfter(start)
                                                ? Promise.resolve()
                                                : Promise.reject(new Error('The end must be after the start.'))
                                        },
                                    })),
                                ].filter(Boolean)}
                            >
                                <TimePicker
                                    className="w-100"
                                    format={TIME_FORMAT}
                                    minuteStep={5}
                                    showNow={false}
                                    placeholder="HH:mm"
                                    onSelect={(value) => setTime(field, value)}
                                    onBlur={keepTypedTime(field)}
                                    // Tab saves the typed time and moves on (the picker would otherwise keep focus in its list)
                                    onKeyDown={(e, preventDefault) => {
                                        if (e.key === 'Tab') {
                                            keepTypedTime(field)(e)
                                            preventDefault()
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                    <Col span={24}>
                        <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Please select a type!' }]}>
                            <Radio.Group>
                                {APPOINTMENT_TYPES.map((type) => <Radio.Button key={type} value={type}>{type}</Radio.Button>)}
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="notes" label="Notes">
                            <Input.TextArea rows={3} placeholder="Anything to prepare or discuss (optional)" maxLength={2000} />
                        </Form.Item>
                    </Col>
                    <Col span={24} className="text-end">
                        <Button onClick={onClose} className="me-2">Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            {isEdit ? 'Save' : 'Book Appointment'}
                        </Button>
                    </Col>
                    {!isEdit && (
                        <Col span={24}>
                            <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>
                                The patient gets an email with the date, time and type.
                            </p>
                        </Col>
                    )}
                </Row>
            </Form>
        </Modal>
    )
}
